import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { Field, Form } from 'react-final-form'
import { classNames } from 'primereact/utils'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { InputSwitch } from 'primereact/inputswitch'
import { Checkbox } from 'primereact/checkbox'
import { MultiSelect } from 'primereact/multiselect'
import { supabase } from 'Services/supabaseClient'
import { t } from 'i18next'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import LayoutContainer from 'Components/LayoutComponent'
import Loader from 'Components/Loader'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

const AddCommunity = ({ setLoading }) => {
  const history = useHistory()

  const categories = [
    { name: 'Regular', key: 'R' },
    { name: 'Gold', key: 'G' },
    { name: 'Platinum', key: 'P' }
  ]

  const [selectedCategories, setSelectedCategories] = useState([])
  const [countriesList, setCountriesList] = useState([])
  const [subscriptionTouched, setSubscriptionTouched] = useState(false)
  const [showDialog, setShowDialog] = useState({ showModal: false, modalCancel: false, modalType: '', message: '', onDismiss: () => {}, onRetry: () => {}, onHide: () => {}, onSuccess: () => {} })
  const [adminOnlyMessages, setAdminOnlyMessages] = useState(false)
  const [countrySpecific, setCountrySpecific] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(null)

  useEffect(() => {
    getAllCountriesList()
  }, [])

  const getAllCountriesList = async () => {
    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    try {
      const countryData = await apiAdapterCoffeeWeb.getAllCountries()

      setCountriesList(countryData.data || [])
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SORRY_COULD_NOT_FETCH_THE_DATA_PLEASE_TRY_AGAIN_LATER'),
        onHide: handleDismiss,
        onRetry: () => getAllCountriesList()
      })
    } finally {
      setLoading(false)
    }
  }

  const onCategoryChange = (e) => {
    setSubscriptionTouched(true)

    const clickedCategory = e.value
    const isSelected = selectedCategories.some((cat) => cat.key === clickedCategory.key)

    if (isSelected) {
      setSelectedCategories([])
    } else {
      // eslint-disable-next-line
      if (clickedCategory.name === 'Regular') {
        setSelectedCategories(categories)
      } else if (clickedCategory.name === 'Gold') {
        setSelectedCategories(categories.filter((cat) => cat.name !== 'Regular'))
      } else if (clickedCategory.name === 'Platinum') {
        setSelectedCategories(categories.filter((cat) => cat.name === 'Platinum'))
      }
    }
  }

  const postCommunityData = async (values) => {
    setShowDialog({ ...showDialog, showModal: false })
    setLoading(true)

    try {
      const subscriptions = selectedCategories.map((category) => category.key)
      const selectedCountriesId = selectedCountry?.map(({ id }) => id?.toString())

      const payload = {
        name: values.communityName,
        community_url: values.communityLogoLink,
        description: values.description,
        required_subscription: subscriptions,
        country_specific: countrySpecific,
        country_id: countrySpecific ? selectedCountriesId : null,
        admin_only_message: adminOnlyMessages
      }

      const { data, error, status } = await supabase.from('communities').insert(payload).select('*').single()

      if (status === 201 && data.length !== 0) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('COMMUNITY_ADDED_SUCCESSFUL'),
          modalCancel: false,
          onSuccess: () => history.push(ROUTE_STRINGS.communityManagement)
        })
      }

      if (error) {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('FAILED_TO_ADD_COMMUNITY'),
          modalCancel: false,
          onDismiss: handleDismiss,
          onRetry: () => postCommunityData(values)
        })
      }
    } catch (err) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_ADD_COMMUNITY'),
        modalCancel: false,
        onDismiss: handleDismiss,
        onRetry: () => postCommunityData(values)
      })
    } finally {
      setLoading(false)
    }
  }

  const validate = (values) => {
    const errors = {}

    if (!values.communityName) {
      errors.communityName = t('COMMUNITY_NAME_REQUIRED')
    }

    if (countrySpecific && !selectedCountry) {
      errors.country = 'Country is required'
    }

    return errors
  }

  const handleCountrySpecificToggle = (value) => {
    setCountrySpecific(value)

    if (!value) {
      setSelectedCountry(null)
    }
  }

  const handleCountryChange = (value) => {
    setSelectedCountry(value)
  }

  const handleCancel = () => {
    history.push({
      pathname: ROUTE_STRINGS.communityManagement
    })
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const isFormFieldValid = (meta) => !!(meta.touched && meta.error)
  const getFormErrorMessage = (meta) => isFormFieldValid(meta) && <small className="p-error">{meta.error}</small>

  const countryOptionTemplate = (country) => {
    if (!country) return null

    return (
      <div className="country-item-template">
        <img alt={country?.nicename} src={country?.flagsUrl} className="flag-img" />
        <span className="country-name">{country?.nicename}</span>
      </div>
    )
  }

  const selectedCountryTemplate = (country) => {
    if (!country) return 'Select Country'

    return (
      <div className="country-value-template">
        <img alt={country?.nicename} src={country?.flagsUrl} className="flag-img" />
        <span className="country-name">{country?.nicename}</span>
      </div>
    )
  }

  return (
    <>
      <LayoutContainer title={t('ADD_COMMUNITY')} breadCrumbsTitle={'addCommunity'}>
        <div className="add-community-form">
          <Form
            onSubmit={postCommunityData}
            validate={validate}
            render={({ handleSubmit, pristine, valid, submitting }) => {
              const submitForm = (e) => {
                e.preventDefault()
                handleSubmit()
              }

              return (
                <>
                  <form onSubmit={handleSubmit} className="form-container">
                    {/* Community Name */}
                    <Field
                      name="communityName"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label">
                            <InputText id="communityName" {...input} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} />
                            <label htmlFor="communityName" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('COMMUNITY_NAME')} <span className="star-required">*</span>
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Community Logo Link */}
                    <Field
                      name="communityLogoLink"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label community-logo-label">
                            <InputText id="communityLogoLink" {...input} className={classNames(isFormFieldValid(meta) ? 'error' : 'community-logo-link')} />
                            <label htmlFor="communityLogoLink" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('COMMUNITY_LOGO_LINK')}
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Description */}
                    <Field
                      name="description"
                      render={({ input, meta }) => (
                        <div className="field">
                          <span className="p-float-label description-float-label">
                            <InputTextarea id="description" {...input} rows={5} cols={30} className={classNames(isFormFieldValid(meta) ? 'error' : 'add-menu-field')} placeholder={t('DESCRIPTION')} />
                            <label htmlFor="description" className={classNames(isFormFieldValid(meta) ? 'p-error' : 'mapping-label')}>
                              {t('DESCRIPTION')}
                            </label>
                          </span>
                          {getFormErrorMessage(meta) && <div className="error-message">{getFormErrorMessage(meta)}</div>}
                        </div>
                      )}
                    />

                    {/* Subscription Category Checkboxes */}
                    <div className="subscription-div">
                      <div className="subscription-checkbox-div">
                        <span>{t('SUBSCRIPTIONS')}</span> <span className="mandate">*</span>
                        {categories.map((category) => (
                          <div key={category.key} className="subscription-checkbox">
                            <Checkbox inputId={category.key} name="category" value={category} onChange={onCategoryChange} checked={selectedCategories.some((item) => item.key === category.key)} className="checkbox-field" />
                            <label htmlFor={category.key} className="checkbox-label">
                              {category.name}
                            </label>
                          </div>
                        ))}
                        {subscriptionTouched && selectedCategories.length === 0 && <div className="checkbox-error-message">{t('SUBSCRIPTION_REQUIRED')}</div>}
                      </div>
                    </div>

                    {/* admin only message toggle switches */}

                    <div className="field toggle-field">
                      <span className="toggle-label">{t('ADMIN_ONLY_MESSAGES')}</span>
                      <InputSwitch id="admin_only_messages_toggle" checked={adminOnlyMessages} onChange={(e) => setAdminOnlyMessages(e.value)} />
                    </div>

                    <div className="field toggle-field">
                      <span className="toggle-label">{t('COUNTRY_SPECIFIC')}</span>

                      <InputSwitch id="country_specific_toggle" checked={countrySpecific} onChange={(e) => handleCountrySpecificToggle(e.value)} />
                    </div>

                    {/* Country Dropdown (show only if toggle is true) */}
                    {countrySpecific && (
                      <div className="field">
                        <MultiSelect id="country" value={selectedCountry} options={countriesList} optionLabel="nicename" filter onChange={(e) => handleCountryChange(e.value)} maxSelectedLabels={3} className="country-dropdown-field" placeholder="Select Country" itemTemplate={(country) => countryOptionTemplate(country)} valueTemplate={(country) => selectedCountryTemplate(country)} />
                      </div>
                    )}
                  </form>

                  <div className="add-community-user-footer">
                    <ButtonComponent type="cancel" onClick={handleCancel} />
                    {/* <ButtonComponent type="submit" onClick={submitForm} disabled={!valid || submitting || pristine || selectedCategories.length === 0} /> */}
                    <ButtonComponent type="submit" onClick={submitForm} disabled={!valid || submitting || pristine || selectedCategories.length === 0 || (countrySpecific && !selectedCountry)} />
                  </div>
                </>
              )
            }}
          />
        </div>
      </LayoutContainer>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(AddCommunity)
